import {
	getAccessToken,
	getLoggedInOrganisation,
	getTokens,
	isTokenExpired
} from "authentication/AuthenticationService";

import { ERR_INTERNET_DISCONNECTED } from "utils/ErrorCodes";

function initOptions(options?: RequestInit): RequestInit {
	if (!options) {
		options = {};
	}
	return options;
}

function setAuthorizationHeader(options: RequestInit) {
	options.headers = {
		...options.headers,
		Authorization: "Bearer " + getAccessToken()
	};
}

function setContentTypeHeader(options: RequestInit, type: string) {
	options.headers = {
		...options.headers,
		"Content-Type": type
	};
}

function setJsonContentOption(
	method: string,
	content: unknown,
	options?: RequestInit
): RequestInit {
	options = initOptions(options);
	options.method = method;
	options.body = JSON.stringify(content);
	setContentTypeHeader(options, "application/json");

	return options;
}

function setFormContentOption(
	method: string,
	content: FormData,
	options?: RequestInit
): RequestInit {
	options = initOptions(options);
	options.method = method;
	options.body = content;
	// setContentTypeHeader(options, "application/octet-stream");

	return options;
}

async function fetchAuthenticated(
	input: RequestInfo,
	options: RequestInit
): Promise<Response> {
	if (isTokenExpired()) {
		await getTokens(getLoggedInOrganisation()).then(tokens => {
			localStorage.setItem("accessToken", tokens.accessToken);
			localStorage.setItem("refreshToken", tokens.refreshToken);
		});
	}

	setAuthorizationHeader(options);
	return fetch(input, options).catch(() => {
		throw new Error(ERR_INTERNET_DISCONNECTED);
	});
}

export function get(url: string, options?: RequestInit): Promise<Response> {
	return fetchAuthenticated(url, initOptions(options));
}

export async function getType<T>(
	url: string,
	options?: RequestInit
): Promise<T> {
	const response = await fetchAuthenticated(url, initOptions(options));
	checkStatus(response, 200);

	return response.json();
}

export function del(url: string, options?: RequestInit): Promise<Response> {
	options = initOptions(options);
	options.method = "DELETE";
	return fetchAuthenticated(url, options);
}

export async function create<T>(
	url: string,
	content: Record<string, unknown>,
	options?: RequestInit
): Promise<T> {
	const response = await fetchAuthenticated(
		url,
		setJsonContentOption("POST", content, options)
	);
	checkStatus(response, 201);
	return response.json();
}

export function post(
	url: string,
	content: unknown = {},
	options?: RequestInit
): Promise<Response> {
	return fetchAuthenticated(
		url,
		setJsonContentOption("POST", content, options)
	);
}

export function postForm(
	url: string,
	content: FormData,
	options?: RequestInit
): Promise<Response> {
	return fetchAuthenticated(
		url,
		setFormContentOption("POST", content, options)
	);
}

export function put(
	url: string,
	content: unknown,
	options?: RequestInit
): Promise<Response> {
	return fetchAuthenticated(
		url,
		setJsonContentOption("PUT", content, options)
	);
}

export function putForm(
	url: string,
	content: FormData,
	options?: RequestInit
): Promise<Response> {
	return fetchAuthenticated(
		url,
		setFormContentOption("PUT", content, options)
	);
}

export function patch(
	url: string,
	content: unknown,
	options?: RequestInit
): Promise<Response> {
	return fetchAuthenticated(
		url,
		setJsonContentOption("PATCH", content, options)
	);
}

export function checkStatus(response: Response, statusCode: number) {
	if (response.status !== statusCode) {
		throw new Error(response.statusText);
	}
}

export function extractHeader(headers: Headers, name: string): string {
	const value = headers.get(name);
	if (value === null) {
		throw new Error("Missing " + name + " header!");
	} else {
		return value;
	}
}
